import React from 'react'

import { Skeleton } from '@mui/material'
import { sumBy } from 'lodash'
import { Camera, File, Loader2Icon, SquarePen } from 'lucide-react'

import { HorseTrainer } from '../../../../../../../components/rider/HorseTrainer/HorseTrainer'
import { TotalPointsInput } from '../components/TotalPointsInput/TotalPointsInput'
import { CustomScoringTotal } from '../../CustomScoringTotal/CustomScoringTotal'

import { getAllRegistrationFees } from '../../../../../../../helpers/registrationFees'
import { generateName } from '../../../../../../../helpers/helpers'
import { cn } from '../../../../../../../lib/utils'

import { IRegistrationByDayEx } from '../../../ManageClinicNOtherOperationsTab'
import {
  IEventFees,
  RegistrationFeesType,
} from '../../../../../../../models/event-fees/event-fees.interface'
import { IRegistrationByDayInterface } from '../../../../../../../models/registrations-by-day/registrationByDay.interface'
import { IEventTeamMemberInterface } from '../../../../../../../hooks/useEventRiderTeamMembers'

import { IMAGE_CONSTS } from '../../../../../../../const/image-const'
import { HORSE_NAME_DEFAULT } from '../../../../../../../models/registrations-by-day/registrationByDay.interface'

export const getColumnsScoring = ({
  type,
  getTime,
  eventTeamMembers,
  eventTeamMembersLoading,
  setRegistrationsByDay,
  registrationsByDay,
  registrationFees,
  onScore,
  scoringLoading,
  uploadLoading,
  fileChangedHandler,
}: {
  type: 'class' | 'rider'
  eventTeamMembers: IEventTeamMemberInterface
  eventTeamMembersLoading: boolean
  setRegistrationsByDay: (value: IRegistrationByDayInterface[]) => void
  registrationsByDay: IRegistrationByDayInterface[]
  registrationFees: IEventFees | null
  onScore: (row: IRegistrationByDayEx) => void
  getTime: (row: IRegistrationByDayInterface) => string
  scoringLoading: string
  uploadLoading: string
  fileChangedHandler: (row: IRegistrationByDayEx, file: FileList | null) => Promise<void>
}) => [
  {
    name: (
      <span className="text-gray-400 font-normal text-[0.875rem] flex items-center">
        <span className="whitespace-nowrap">Ride Time</span>
      </span>
    ),
    cell: (row: IRegistrationByDayEx) => (
      <div className="flex items-center">
        {row.loading ? (
          <Skeleton
            variant="rounded"
            width={60}
            height={20}
            className="mb-2"
            style={{ backgroundColor: '#F1F3F8' }}
          />
        ) : (
          <div className="text-SeabiscuitDark200ThemeColor text-nr justify-center w-full flex capitalize">
            {(type === 'rider' ? row.isScratched : row.orderOfGoScratched) ? (
              <span className="text-SeabiscuitDark200ThemeColor/50">Scratched</span>
            ) : (
              <span className={cn(row.isScratched && 'line-through')}>{getTime(row)}</span>
            )}
          </div>
        )}
      </div>
    ),
    width: '10%',
  },
  {
    name: (
      <span className="text-gray-400 font-normal text-[0.875rem] flex items-center">
        <span className="whitespace-nowrap">Rider & Back Number</span>
      </span>
    ),
    cell: (row: IRegistrationByDayEx) => {
      return (
        <div className="flex items-center gap-4">
          <span className="w-11 h-11 rounded-full block shrink-0 my-2 overflow-hidden">
            {row.loading ? (
              <Skeleton
                variant="circular"
                width={44}
                height={44}
                className="mb-2"
                style={{ backgroundColor: '#F1F3F8' }}
              />
            ) : (
              <img
                src={row.riderProfilePicture ?? IMAGE_CONSTS.PLACEHOLDERS.USER}
                alt="icons"
                className="object-cover w-full h-full rounded-full"
                onError={(e) =>
                  ((e.target as any).src = `https://ui-avatars.com/api/?name=${generateName(
                    row.riderName ?? ''
                  )}&background=FFFFFF&format=svg&bold=true&color=BCC6D5&rounded=true`)
                }
              />
            )}
          </span>

          {row.loading ? (
            <Skeleton
              variant="rounded"
              width={100}
              height={20}
              style={{ backgroundColor: '#F1F3F8' }}
            />
          ) : (
            <div className="flex flex-col">
              <span className="text-SeabiscuitDark200ThemeColor text-base">{row?.riderName}</span>
              <span className="text-SeabiscuitDark200ThemeColor/80 text-nr flex items-center">
                {Number(row.backNumber) > 0 ? (
                  <>{row.backNumber}</>
                ) : (
                  <span className="inline-flex items-center text-SeabiscuitDark200ThemeColor/60">
                    No back number
                  </span>
                )}
              </span>
            </div>
          )}
        </div>
      )
    },
    width: type === 'rider' ? '19%' : '20%',
  },
  {
    name: (
      <span className="text-gray-400 font-normal text-[0.875rem] flex items-center">
        <span className="whitespace-nowrap">Horse & Trainer</span>
      </span>
    ),
    cell: (row: IRegistrationByDayEx) => {
      return (
        <div className="flex items-center gap-4">
          <span className="w-11 h-11 rounded-full block shrink-0 my-2 overflow-hidden">
            {row.loading ? (
              <Skeleton
                variant="circular"
                width={44}
                height={44}
                className="mb-2"
                style={{ backgroundColor: '#F1F3F8' }}
              />
            ) : (
              <img
                src={row.horseProfilePicture ?? IMAGE_CONSTS.PLACEHOLDERS.USER}
                alt="icons"
                className="object-cover w-full h-full rounded-full"
                onError={(e) =>
                  ((e.target as any).src = `https://ui-avatars.com/api/?name=${generateName(
                    row.horseName ?? ''
                  )}&background=FFFFFF&format=svg&bold=true&color=BCC6D5&rounded=true`)
                }
              />
            )}
          </span>

          {row.loading ? (
            <Skeleton
              variant="rounded"
              width={100}
              height={20}
              style={{ backgroundColor: '#F1F3F8' }}
            />
          ) : (
            <div className="flex flex-col">
              <span className="text-SeabiscuitDark200ThemeColor  text-base">
                {row?.horseName ?? HORSE_NAME_DEFAULT}
              </span>
              <span className="text-SeabiscuitDark200ThemeColor/60 text-nr flex items-center">
                <HorseTrainer
                  eventTeamMembers={eventTeamMembers}
                  eventTeamMembersLoading={eventTeamMembersLoading}
                  rider={row}
                />
              </span>
            </div>
          )}
        </div>
      )
    },
    width: type === 'rider' ? '15%' : '25%',
  },
  ...(type === 'rider'
    ? [
        {
          id: 'class',
          name: (
            <span className="text-gray-400 font-normal text-[0.875rem] flex items-center justify-center px-3">
              <span className="whitespace-nowrap">Class</span>
            </span>
          ),
          cell: (row: IRegistrationByDayEx) => (
            <div className="px-3">
              <span className="text-SeabiscuitDark200ThemeColor text-sm">
                {row.registrationByDayName}
              </span>
              {row.fee?.order ? (
                <span className="m-1 whitespace-nowrap text-[14px] text-SeabiscuitMainThemeColor bg-SeabiscuitMainThemeColor/5 rounded-full py-0.5 px-3">
                  {row.fee.order}
                </span>
              ) : null}
            </div>
          ),
          width: '21%',
        },
      ]
    : []),
  {
    name: (
      <div className="text-gray-400 font-normal text-[0.875rem] flex items-center justify-end w-full px-3">
        <span className="whitespace-nowrap">Score card</span>
      </div>
    ),
    cell: (row: IRegistrationByDayEx) => {
      const flattenedFees = getAllRegistrationFees(registrationFees?.registrationFees || [])
      // Find the fee whose UUID matches the registration's UUID
      const matchingFee = flattenedFees?.find(
        (feeItem: RegistrationFeesType) => feeItem.uuid === row.uuid
      )

      return (
        <div className="justify-end items-center w-full flex text-right px-3">
          {row.loading ? (
            <Skeleton
              variant="rounded"
              width={80}
              height={20}
              style={{ backgroundColor: '#F1F3F8' }}
            />
          ) : (
            <div className="text-SeabiscuitDark200ThemeColor/50 text-[14px]">
              {matchingFee?.scoreCard === 'test-of-choice' ||
              row?.fee?.scoreCard === 'test-of-choice'
                ? row.score?.sheet?.name || 'Test of Choice'
                : row.score?.sheet?.name || matchingFee?.scoreCard || '-'}
            </div>
          )}
        </div>
      )
    },
    width: type === 'rider' ? '15%' : '25%',
  },
  {
    name: (
      <div className="text-gray-400 font-normal text-[0.875rem] flex items-center justify-end w-full">
        {type === 'class' ? (
          <button className="whitespace-nowrap flex items-center gap-1 hover:opacity-70 transition-all">
            Score
          </button>
        ) : (
          <span className="whitespace-nowrap">Score</span>
        )}
      </div>
    ),
    cell: (row: IRegistrationByDayEx) => {
      const flattenedFees = getAllRegistrationFees(registrationFees?.registrationFees || [])
      // Find the fee whose UUID matches the registration's UUID
      const matchingFee = flattenedFees?.find(
        (feeItem: RegistrationFeesType) => feeItem.uuid === row.uuid
      )

      let max = 0
      if (row.score?.judges) {
        max = sumBy(row.score.judges[0]?.sections, (section) => section.max)
      }

      const rowFee = type === 'rider' ? row.fee : matchingFee
      const judge = row.score?.judges?.[0]

      const isCustomScoreCard =
        rowFee?.scoreCard &&
        rowFee?.scoreCard !== 'Default' &&
        rowFee?.scoreCard !== 'Equitation' &&
        rowFee?.scoreCard !== 'Hunters'

      return (
        <div className="justify-end items-center w-full flex min-w-[250px]">
          {row.loading ? (
            <Skeleton
              variant="rounded"
              width={80}
              height={20}
              style={{ backgroundColor: '#F1F3F8' }}
            />
          ) : (
            <>
              {isCustomScoreCard ? (
                <button
                  className="flex items-center flex-col cursor-pointer text-SeabiscuitDark200ThemeColor text-sm text-right hover:opacity-70 transition-all"
                  onClick={() => onScore(row)}
                >
                  {row.score?.sheet?.type === 'hunter-jumper' && judge ? (
                    <CustomScoringTotal right row={row} />
                  ) : (
                    <>
                      {Number(row.score?.totalPoints) > 0 ? (
                        <div className="flex items-center gap-2">
                          <img
                            className="w-5 relative -top-[1px]"
                            src="/assets/cp_icons/Lock.svg"
                            alt=""
                          />
                          <span className="font-medium">{row.score?.totalPoints}</span>{' '}
                          <p className="text-gray-500">•</p>{' '}
                          <p>
                            {((Number(row.score?.totalPoints) / Number(max)) * 100).toFixed(3)}%
                          </p>
                          {row.score?.scoreStatus && (
                            <p className="opacity-50">
                              <span>•</span> {row.score.scoreStatus}
                            </p>
                          )}
                        </div>
                      ) : row.score?.scoreStatus ? (
                        <p className="opacity-50 no-underline hover:underline">
                          {row.score.scoreStatus}
                        </p>
                      ) : (
                        <span className="opacity-50 no-underline hover:underline placeholder:text-sm text-sm">
                          Enter number
                        </span>
                      )}
                    </>
                  )}
                </button>
              ) : (
                <div className="flex items-center flex-col cursor-pointer text-SeabiscuitDark200ThemeColor text-sm text-right hover:opacity-70 transition-all">
                  <TotalPointsInput
                    row={row}
                    setRegistrationsByDay={setRegistrationsByDay}
                    registrationsByDay={registrationsByDay}
                    fee={matchingFee}
                  />
                </div>
              )}
            </>
          )}
          <div className="ml-4 flex items-center gap-3">
            {row.loading || scoringLoading === row.id ? (
              <Loader2Icon className="animate-spin w-4" />
            ) : (
              <button type="button" onClick={() => onScore(row)}>
                <SquarePen className="w-4 h-4 text-SeabiscuitDark200ThemeColor/50 hover:text-black transition-all" />
              </button>
            )}
            {row.score?.file ? (
              <a href={row.score.file} target="_blank" rel="noopener noreferrer">
                <File className="w-4 h-4 text-SeabiscuitDark200ThemeColor/50 hover:text-black transition-all" />
              </a>
            ) : (
              <label className="cursor-pointer">
                <input
                  id="fileInput"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(event) => fileChangedHandler(row, event.target.files)}
                />
                {row.loading || uploadLoading === row.id ? (
                  <Loader2Icon className="animate-spin w-[18px]" />
                ) : (
                  <Camera className="w-[18px] h-[18px] text-SeabiscuitDark200ThemeColor/50 hover:text-black transition-all" />
                )}
              </label>
            )}
          </div>
        </div>
      )
    },
    width: '20%',
  },
]
